import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { DateTime } from 'luxon'
import type { CnAutomationDto } from 'store/cn/cn.types'
import {
  useCancelCnAutomationModificationMutation,
  useListCnAutomationQuery,
  usePatchCnAutomationAppointmentMutation,
} from 'store/cn/cn.api'
import {
  CN_TIME_SLOTS,
  firstClockFromTimeSlot,
  formatSlotsForDisplay,
  parsePickerStartsFromStored,
  sameStartSets,
  snapStartFromRow,
  sortPickerStarts,
  startsToStoredSlots,
} from './cn-time-slots'

type StatusTab = 'confirmed' | 'processing' | 'completed'

type CnRow = {
  id: string
  rv: string
  container: string
  terminalLabel: string
  eventDateStr: string
  timeSlotRaw: string
  pendingTimeSlotsRaw: string
  status: string
  subStatus: string
  activity: string
  booking: string
  appt: DateTime
  cnModified: boolean
  allSlotsFailed: boolean
}

const LOCATIONS = ['Brampton', 'Mississauga', 'Malport'] as const

const TERMINAL_BY_LOCATION: Record<(typeof LOCATIONS)[number], string> = {
  Brampton: 'BRAMPTON',
  Mississauga: 'MISSISSAUGA',
  Malport: 'MALPORT',
}

const TERMINAL_DISPLAY: Record<string, string> = {
  BRAMPTON: 'Brampton',
  MISSISSAUGA: 'Mississauga',
  MALPORT: 'Malport',
}

/** Labels as they appear in the CN Gate Appointment terminal dropdown (used by portal sync). */
const CN_PORTAL_TERMINAL_LABEL: Record<(typeof LOCATIONS)[number], string> = {
  Brampton: 'BRAMPTON INTERMODAL',
  Mississauga: 'MISSISSAUGA INT SER',
  Malport: 'MALPORT YARD',
}

const STATUS_BY_TAB: Record<StatusTab, string> = {
  confirmed: 'Confirmed',
  processing: 'Processing',
  completed: 'Completed',
}

function dtoToRow(d: CnAutomationDto): CnRow {
  const datePart = d.eventDate ?? DateTime.now().toISODate()!
  let timePart = '00:00'
  const fromSlot = firstClockFromTimeSlot(d.timeSlot)
  if (fromSlot) {
    timePart = fromSlot
  } else if (d.timeSlot && /^\d{1,2}:\d{2}$/.test(d.timeSlot.trim())) {
    const [hh, mm] = d.timeSlot.trim().split(':')
    timePart = `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`
  }
  const iso = `${datePart}T${timePart}`
  let appt = DateTime.fromISO(iso)
  if (!appt.isValid) {
    appt = DateTime.now()
  }
  const term = d.terminal ?? ''
  return {
    id: d.id,
    rv: d.rvNumber ?? '—',
    container: d.equipmentNumber ?? '',
    terminalLabel: TERMINAL_DISPLAY[term] ?? (term || '—'),
    eventDateStr: d.eventDate ?? '—',
    timeSlotRaw: d.timeSlot ?? '—',
    pendingTimeSlotsRaw: d.pendingTimeSlots ?? '',
    status: d.status ?? '—',
    subStatus: d.subStatus ?? '—',
    activity: d.activity ?? '—',
    booking: d.bookingNumber ?? '',
    appt,
    cnModified: d.cnModified === true,
    allSlotsFailed: d.allSlotsFailed === true,
  }
}

function buildCalendarCells(month: DateTime): (number | null)[] {
  const first = month.startOf('month')
  const pad = first.weekday % 7
  const daysInMonth = month.daysInMonth!
  const cells: (number | null)[] = []
  for (let i = 0; i < pad; i += 1) cells.push(null)
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

const CnAutomationPage = () => {
  const apiBase = process.env.REACT_APP_ONBOARDING_SERVICE_API
  const [statusTab, setStatusTab] = useState<StatusTab>('confirmed')
  const [location, setLocation] = useState<(typeof LOCATIONS)[number]>('Brampton')
  const [search, setSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<CnRow | null>(null)
  const [calendarMonth, setCalendarMonth] = useState(() =>
    DateTime.now().startOf('month')
  )
  const [pickerDate, setPickerDate] = useState(() => DateTime.now().startOf('day'))
  const [pickerTimes, setPickerTimes] = useState<string[]>([])
  const [pickerError, setPickerError] = useState<string | null>(null)
  const [importBanner, setImportBanner] = useState<{ severity: 'success' | 'error'; text: string } | null>(null)

  const terminal = TERMINAL_BY_LOCATION[location]
  const processStatus = STATUS_BY_TAB[statusTab]

  const {
    data: automationList,
    isFetching: listFetching,
    error: listError,
    refetch: refetchAutomationList,
  } = useListCnAutomationQuery(
    { terminal, processStatus },
    { skip: !apiBase }
  )

  const [patchAppt, { isLoading: patchingAppt }] = usePatchCnAutomationAppointmentMutation()
  const [cancelModification, { isLoading: cancellingModification }] =
    useCancelCnAutomationModificationMutation()

  const rows = useMemo(
    () => (automationList ?? []).map(dtoToRow),
    [automationList]
  )

  useEffect(() => {
    setSearch('')
  }, [statusTab, location])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (r) =>
        r.rv.toLowerCase().includes(q) ||
        r.container.toLowerCase().includes(q) ||
        r.booking.toLowerCase().includes(q) ||
        r.activity.toLowerCase().includes(q) ||
        r.terminalLabel.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q) ||
        r.subStatus.toLowerCase().includes(q) ||
        r.timeSlotRaw.toLowerCase().includes(q) ||
        r.pendingTimeSlotsRaw.toLowerCase().includes(q)
    )
  }, [rows, search])

  const calendarCells = useMemo(
    () => buildCalendarCells(calendarMonth),
    [calendarMonth]
  )

  const openPicker = (row: CnRow) => {
    setEditingRow(row)
    const d = row.appt.startOf('day')
    setPickerDate(d)
    setCalendarMonth(d.startOf('month'))
    const slotSource =
      statusTab === 'processing' && row.pendingTimeSlotsRaw
        ? row.pendingTimeSlotsRaw
        : row.timeSlotRaw
    const existing = parsePickerStartsFromStored(slotSource)
    setPickerTimes(
      existing.length > 0 ? existing : [snapStartFromRow(slotSource, row.appt.hour)]
    )
    setPickerError(null)
    setPickerOpen(true)
  }

  const closePicker = () => {
    setPickerOpen(false)
    setEditingRow(null)
    setPickerError(null)
  }

  const onCancelModification = async (row: CnRow) => {
    try {
      await cancelModification({ automationId: row.id }).unwrap()
      setStatusTab('confirmed')
    } catch {
      setImportBanner({
        severity: 'error',
        text: 'Could not cancel modification. Try again.',
      })
    }
  }

  const applyPicker = async () => {
    if (!editingRow) return
    if (pickerTimes.length === 0) {
      setPickerError('Select at least one time slot')
      return
    }
    setPickerError(null)
    const eventDate = pickerDate.toISODate()
    if (!eventDate) {
      setPickerError('Invalid date')
      return
    }
    const priorDate = editingRow.appt.toISODate()
    const priorSlotRaw =
      editingRow.pendingTimeSlotsRaw || editingRow.timeSlotRaw
    const priorStarts = parsePickerStartsFromStored(priorSlotRaw)
    const sorted = sortPickerStarts(pickerTimes)
    const timeSlotWindow = startsToStoredSlots(sorted)
    const appointmentChanged =
      eventDate !== priorDate || !sameStartSets(sorted, priorStarts)
    try {
      await patchAppt({
        automationId: editingRow.id,
        body: { eventDate, timeSlot: timeSlotWindow },
      }).unwrap()
      if (appointmentChanged) {
        setStatusTab('processing')
      }
      closePicker()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err.data as { message?: string })?.message ?? '')
          : ''
      setPickerError(msg || 'Could not update appointment. Try again.')
    }
  }

  const apptColumnLabel =
    statusTab === 'processing' ? 'New Appt. Date' : 'Appt. Date'

  const listErrMsg =
    listError && 'data' in listError
      ? String((listError.data as { message?: string })?.message ?? '')
      : ''

  const onRefreshFromCn = async () => {
    setImportBanner(null)
    try {
      const { data, error } = await refetchAutomationList()
      if (error) throw error
      setImportBanner({
        severity: 'success',
        text: `Reloaded ${(data ?? []).length} row(s) from the database.`,
      })
    } catch {
      setImportBanner({ severity: 'error', text: 'Could not reload from database. Try again.' })
    }
  }

  if (!apiBase) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="warning">
          Set <code>REACT_APP_ONBOARDING_SERVICE_API</code> to your onboarding-service base URL (e.g.
          http://localhost:8081).
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        bgcolor: 'background.default',
        px: { xs: 1, sm: 2, md: 3 },
        py: 2,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        CN automation
      </Typography>

      {importBanner && (
        <Alert severity={importBanner.severity} sx={{ mb: 2 }} onClose={() => setImportBanner(null)}>
          {importBanner.text}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ alignItems: 'stretch', gap: 0, flex: 1, minHeight: 0, maxHeight: 'calc(100vh - 120px)' }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 220 },
            flexShrink: 0,
            borderRight: { md: '1px solid #EBEBEB' },
            borderBottom: { xs: '1px solid #EBEBEB', md: 'none' },
            display: 'flex',
            flexDirection: 'column',
            pr: { md: 2 },
            pb: { xs: 2, md: 0 },
          }}
        >
          <Tabs
            value={statusTab}
            onChange={(_, v) => setStatusTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, fontSize: 14, py: 0.5 },
            }}
          >
            <Tab label="Confirmed" value="confirmed" />
            <Tab label="Processing" value="processing" />
            <Tab label="Completed" value="completed" />
          </Tabs>
          <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} sx={{ mt: 2, flex: 1, flexWrap: 'wrap' }}>
            {LOCATIONS.map((loc) => (
              <Tooltip
                key={loc}
                title={`Table filter only. CN portal uses: ${CN_PORTAL_TERMINAL_LABEL[loc]}`}
                placement="right"
              >
                <Button
                  onClick={() => setLocation(loc)}
                  variant={location === loc ? 'contained' : 'outlined'}
                  color="primary"
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: 2,
                    borderColor: '#E0E0E0',
                    ...(location === loc
                      ? {}
                      : {
                          bgcolor: '#F5F5F5',
                          color: 'text.primary',
                          border: 'none',
                          '&:hover': { bgcolor: '#EEEEEE' },
                        }),
                  }}
                >
                  {loc}
                </Button>
              </Tooltip>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            pl: { md: 2 },
            pt: { xs: 2, md: 0 },
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip
                title="Reloads current table from database only (no CN automation run)."
                placement="bottom"
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none' }}
                    onClick={() => onRefreshFromCn()}
                    disabled={listFetching}
                  >
                    {listFetching ? 'Refreshing…' : 'Refresh'}
                  </Button>
                </span>
              </Tooltip>
              {listFetching && <CircularProgress size={22} />}
            </Box>
            <TextField
              size="small"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Stack>

          {listError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {listErrMsg || 'Could not load CN automation data.'}
            </Alert>
          )}

          <TableContainer
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              maxHeight: { xs: 'calc(100vh - 280px)', md: 'calc(100vh - 220px)' },
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{ minWidth: 640, '& tbody tr:nth-of-type(even)': { bgcolor: '#FAFAFA' } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>RV number</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>{apptColumnLabel}</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>Activity</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>Booking#</TableCell>
                  <TableCell sx={{ bgcolor: 'background.paper' }}>
                    {statusTab === 'confirmed' ? 'Status' : 'Action'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No rows for this terminal and status. Use Refresh or choose another filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filteredRows.map((row) => {
                  const apptSlotRaw =
                    statusTab === 'processing' && row.pendingTimeSlotsRaw
                      ? row.pendingTimeSlotsRaw
                      : row.timeSlotRaw
                  const apptStart = formatSlotsForDisplay(apptSlotRaw)
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ color: 'primary.main', fontWeight: 600, display: 'block' }}
                        >
                          {row.rv}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.container || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                          <IconButton
                            size="small"
                            aria-label="Edit appointment"
                            onClick={() => openPicker(row)}
                            sx={{ color: 'primary.main', mt: 0.25, p: 0.5 }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {row.eventDateStr}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {apptStart}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.activity}</TableCell>
                      <TableCell>{row.booking || '—'}</TableCell>
                      <TableCell>
                        {statusTab === 'confirmed' && row.allSlotsFailed && (
                          <Typography
                            component="span"
                            sx={{ color: 'error.main', fontWeight: 600, textTransform: 'lowercase' }}
                          >
                            failed
                          </Typography>
                        )}
                        {statusTab === 'confirmed' && !row.allSlotsFailed && row.cnModified && (
                          <Typography
                            component="span"
                            sx={{ color: 'success.main', fontWeight: 600, textTransform: 'lowercase' }}
                          >
                            modified
                          </Typography>
                        )}
                        {statusTab === 'processing' && row.allSlotsFailed && (
                          <Typography
                            component="span"
                            sx={{ color: 'error.main', fontWeight: 600, textTransform: 'lowercase' }}
                          >
                            failed
                          </Typography>
                        )}
                        {statusTab === 'processing' && !row.allSlotsFailed && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            disabled={cancellingModification}
                            sx={{ textTransform: 'none' }}
                            onClick={() => onCancelModification(row)}
                          >
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>

      <Dialog open={pickerOpen} onClose={closePicker} maxWidth="md" fullWidth>
        <DialogContent sx={{ bgcolor: '#F0F0F0', pt: 3 }}>
          {pickerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pickerError}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box sx={{ flex: 1, bgcolor: '#F5F5F5', borderRadius: 2, p: 2 }}>
              <Typography color="text.secondary" sx={{ mb: 1, fontSize: 15 }}>
                Select Date
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setCalendarMonth((m) => m.minus({ months: 1 }).startOf('month'))
                  }
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography fontWeight={600}>
                  {calendarMonth.toFormat('MMMM yyyy')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    setCalendarMonth((m) => m.plus({ months: 1 }).startOf('month'))
                  }
                >
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.5,
                  textAlign: 'center',
                }}
              >
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((d) => (
                  <Typography
                    key={d}
                    variant="caption"
                    color="text.secondary"
                    sx={{ py: 0.5 }}
                  >
                    {d}
                  </Typography>
                ))}
                {calendarCells.map((day, idx) => {
                  if (day === null) {
                    return <Box key={`e-${calendarMonth.toISODate()}-${idx}`} />
                  }
                  const cellDate = calendarMonth.set({ day })
                  const selected = cellDate.hasSame(pickerDate, 'day')
                  return (
                    <Button
                      key={`${calendarMonth.toISODate()}-${day}`}
                      variant={selected ? 'contained' : 'text'}
                      onClick={() => setPickerDate(cellDate.startOf('day'))}
                      sx={{
                        minWidth: 0,
                        p: 0.5,
                        fontSize: 14,
                        borderRadius: 1,
                        ...(!selected && { color: 'text.primary' }),
                      }}
                    >
                      {day}
                    </Button>
                  )
                })}
              </Box>
            </Box>

            <Box sx={{ flex: 1, bgcolor: '#F5F5F5', borderRadius: 2, p: 2, minHeight: 0 }}>
              <Typography color="text.secondary" sx={{ mb: 1, fontSize: 15 }}>
                Select time slot (multiple allowed)
              </Typography>
              <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 0.5,
                  }}
                >
                  {CN_TIME_SLOTS.map(({ start }) => {
                    const checked = pickerTimes.includes(start)
                    return (
                      <FormControlLabel
                        key={start}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => {
                              setPickerTimes((prev) =>
                                checked
                                  ? prev.filter((t) => t !== start)
                                  : sortPickerStarts([...prev, start])
                              )
                            }}
                          />
                        }
                        label={start}
                        sx={{ m: 0, '& .MuiTypography-root': { fontSize: 14 } }}
                      />
                    )
                  })}
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#F0F0F0', px: 3, pb: 2 }}>
          <Button onClick={closePicker} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={applyPicker} disabled={patchingAppt}>
            {patchingAppt ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CnAutomationPage
